// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { useConst, useStepsData } from '@shared/ui-kit';
import {
  sustainabilitySchema,
  type SustainabilityFormData,
} from '@shared/advisory/account-workflow/types';
import {
  ONBOARDING_QUESTIONS,
  OnboardingQuestionBlock,
} from '@shared/advisory/account-workflow/types';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const useSustainability = () => {
  const {
    markStepAsCompleted,
    getFormData,
    saveFormData,
    saveAdvisoryContractData,
    isUpdatingAdvisoryContract,
  } = useAccountWorkflowData();
  const { next } = useStepsData();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const savedData = getFormData('sustainability');

  const form = useForm<SustainabilityFormData>({
    resolver: zodResolver(sustainabilitySchema),
    mode: 'onChange',
    defaultValues: savedData || {},
  });

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    (data: SustainabilityFormData) => {
      saveFormData('sustainability', data);

      saveAdvisoryContractData(
        { sustainability: data },
        { calculateTestResults: true },
      )
        .then(() => {
          markStepAsCompleted('sustainability');
          next();
        })
        .catch(() => {
          enqueueSnackbar(
            t('onboarding-questions.sustainability.save-error', {
              ns: 'components',
            }),
            {
              variant: 'error',
            },
          );
        });
    },
    [
      enqueueSnackbar,
      markStepAsCompleted,
      next,
      saveAdvisoryContractData,
      saveFormData,
      t,
    ],
  );

  const questions = useConst(
    () => ONBOARDING_QUESTIONS.get(OnboardingQuestionBlock.Sustainability)!,
  );

  const sustainabilityPreferences = useWatch({
    control: form.control,
    name: 'q_sustainability_preferences',
  });

  const sustainabilityCriteria = useWatch({
    control: form.control,
    name: 'q_sustainability_criteria',
  }) as string[] | undefined;

  const showConditionalSection =
    sustainabilityPreferences === 'determine_sustainability_criteria';

  useEffect(() => {
    if (
      showConditionalSection &&
      form.getValues('q_sustainability_percentage') === undefined
    ) {
      form.setValue('q_sustainability_percentage', 0, { shouldValidate: true });
    }
  }, [showConditionalSection, form]);

  const questionsMap = useMemo(() => {
    const preferencesQuestion = questions.find(
      q => q.id === 'q_sustainability_preferences',
    );
    const percentageQuestion = questions.find(
      q => q.id === 'q_sustainability_percentage',
    );
    const criteriaQuestion = questions.find(
      q => q.id === 'q_sustainability_criteria',
    );
    const taxonomyQuestion = questions.find(q => q.id === 'q_taxonomy_percentage');
    const sfdrQuestion = questions.find(q => q.id === 'q_sfdr_percentage');
    const piasQuestion = questions.find(q => q.id === 'q_pias_preference');

    return {
      preferencesQuestion,
      percentageQuestion,
      criteriaQuestion,
      taxonomyQuestion,
      sfdrQuestion,
      piasQuestion,
    };
  }, [questions]);

  return {
    form,
    onNext,
    isFormValid,
    questions,
    sustainabilityPreferences,
    sustainabilityCriteria,
    showConditionalSection,
    isLoading: isUpdatingAdvisoryContract,
    ...questionsMap,
  };
};
